import {
  isReferenceableThreadId,
  type ConversationReference,
} from "@cleo/shared/conversationReference";

// Cleo Can 53 (drag): a conversation dragged from the sidebar into the
// composer becomes a reference chip. Two gestures reach the same composer door.
//
// - A World row is already a pointer-sort item (dnd-kit), so its drag cannot
//   also be a native drag. When the sort drag ends over a composer, the rail
//   hands the conversation over through a DOM event, so neither side imports
//   the other.
// - A Local row has no sort, so it is a native drag carrying the payload below.
//
// Nothing is sent and nothing changes in the dragged conversation.

export const CONVERSATION_DRAG_TYPE = "application/x-cleo-conversation";
export const CONVERSATION_DROP_EVENT = "cleo-conversation-drop";
const DROP_TARGET_ATTRIBUTE = "data-conversation-drop";
const DROP_OVER_ATTRIBUTE = "data-conversation-drop-over";

export function conversationDropTargetProps() {
  return { [DROP_TARGET_ATTRIBUTE]: "true" } as const;
}

function findDropTarget(point: { x: number; y: number }): HTMLElement | null {
  return (
    document
      .elementFromPoint(point.x, point.y)
      ?.closest<HTMLElement>(`[${DROP_TARGET_ATTRIBUTE}]`) ?? null
  );
}

let overTarget: HTMLElement | null = null;

/** Highlights the composer under the pointer while a sort drag is over it. */
export function moveConversationDrag(point: { x: number; y: number } | null) {
  const target = point === null ? null : findDropTarget(point);
  if (target === overTarget) return;
  overTarget?.removeAttribute(DROP_OVER_ATTRIBUTE);
  target?.setAttribute(DROP_OVER_ATTRIBUTE, "true");
  overTarget = target;
}

/** True when a composer accepted the drop. */
export function dropConversationOnComposer(
  point: { x: number; y: number },
  reference: ConversationReference,
): boolean {
  moveConversationDrag(null);
  const target = findDropTarget(point);
  if (!target || !isReferenceableThreadId(reference.threadId)) return false;
  target.dispatchEvent(new CustomEvent(CONVERSATION_DROP_EVENT, { detail: reference }));
  return true;
}

export function readConversationDropDetail(event: Event): ConversationReference | null {
  const detail = (event as CustomEvent<unknown>).detail;
  if (typeof detail !== "object" || detail === null) return null;
  const { threadId, title } = detail as { threadId?: unknown; title?: unknown };
  return typeof threadId === "string" &&
    typeof title === "string" &&
    isReferenceableThreadId(threadId)
    ? { threadId, title }
    : null;
}

export interface ConversationDragSourceEvent {
  readonly dataTransfer: {
    setData(format: string, data: string): void;
    effectAllowed: string;
  } | null;
}

/** Makes a row that is not a sort item a native drag source for its conversation. */
export function conversationDragSourceProps(reference: ConversationReference | null) {
  if (reference === null || !isReferenceableThreadId(reference.threadId)) return {};
  return {
    draggable: true,
    onDragStart: (event: ConversationDragSourceEvent) => {
      if (!event.dataTransfer) return;
      event.dataTransfer.effectAllowed = "copy";
      event.dataTransfer.setData(CONVERSATION_DRAG_TYPE, JSON.stringify(reference));
    },
  } as const;
}

export interface ConversationDragEvent {
  readonly dataTransfer: {
    readonly types: ReadonlyArray<string>;
    getData(format: string): string;
    dropEffect: string;
  };
  readonly nativeEvent: { stopPropagation(): void };
  preventDefault(): void;
  stopPropagation(): void;
}

export interface ConversationDropHost {
  addConversation(reference: ConversationReference): void;
  setDragActive(active: boolean): void;
}

const carries = (event: ConversationDragEvent) =>
  event.dataTransfer.types.includes(CONVERSATION_DRAG_TYPE);

function parseDragPayload(raw: string): ConversationReference | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { threadId, title } = value as { threadId?: unknown; title?: unknown };
    return typeof threadId === "string" &&
      typeof title === "string" &&
      isReferenceableThreadId(threadId)
      ? { threadId, title }
      : null;
  } catch {
    return null;
  }
}

/** Each handler answers true when the drag was a conversation and has been handled. */
export function makeComposerConversationDragHandlers(host: ConversationDropHost) {
  const claim = (event: ConversationDragEvent): boolean => {
    if (!carries(event)) return false;
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopPropagation();
    return true;
  };
  return {
    onDragEnter(event: ConversationDragEvent): boolean {
      if (!claim(event)) return false;
      host.setDragActive(true);
      return true;
    },
    onDragOver(event: ConversationDragEvent): boolean {
      if (!claim(event)) return false;
      event.dataTransfer.dropEffect = "copy";
      host.setDragActive(true);
      return true;
    },
    onDrop(event: ConversationDragEvent): boolean {
      if (!claim(event)) return false;
      host.setDragActive(false);
      const reference = parseDragPayload(event.dataTransfer.getData(CONVERSATION_DRAG_TYPE));
      if (reference) host.addConversation(reference);
      return true;
    },
  };
}
